import THDStorageUtils, { LOCAL_STORAGE } from '@thd-olt-global/thd-storage-utils';
import { FALLBACK_USER_ID, LEGACY_PAGE_TYPES } from '../components/MagicApronAssistant/constants';
import { logger } from './logger';

export const getMcvisID = () => {
  return typeof window !== 'undefined'
    ? window.THDCustomer?.default?.mcvisID || FALLBACK_USER_ID : FALLBACK_USER_ID;
};

export const getSvocId = () => {
  return typeof window !== 'undefined' ? window.THDCustomer?.default?.svocID || null : null;
};

export const getUserName = () => {
  return typeof window !== 'undefined' ? window.THDCustomer?.default?.name || null : null;
};

// Lower-cased so callers can compare against a literal host. Read through this
// helper rather than window.location directly so it can be stubbed in tests —
// jsdom's window.location is not redefinable.
export const getHostname = () => {
  return typeof window !== 'undefined'
    ? (window.location?.hostname || '').toLowerCase() : '';
};

export const getPagePath = () => {
  return typeof window !== 'undefined'
    ? new URL(window.document.documentURI).pathname
    : '';
};

// The assistant renders directly in the host page, so this is the same path as
// getPagePath(). Kept until the remaining window.parent references — left over
// from moving the assistant out of an iframe — are cleaned up.
export const getParentPagePath = getPagePath;

// Fallback for the window between page load and analytics initialising, and for
// any experience that never sets digitalData. Mirrors getPageType() in
// thd-analytics (src/components/thd-analytics-utils.js). Order is significant:
// narrower rules first, exactly as in the source.
const PAGE_TYPE_RULES = [
  [/^\/s\//, 'search'],
  [/^\/b\/.*Ntt-/, 'search'],
  [/^\/p\/paint\/config\//, 'magic pip'],
  [/^\/p\/rental\/reservations\//, 'tool:productrental:reservation'],
  [/^\/p\/rental\//, 'tool:productrental:pip'],
  [/^\/p\/sets/, 'bip'],
  [/^\/p\//, 'pip'],
  [/^\/pep\//, 'pep'],
  [/^\/b\//, 'browse'],
  [/^\/c\//, 'content'],
  [/^\/l\//, 'storefinder'],
  [/^\/collection\//, 'collection'],
  [/^\/compare\//, 'compare'],
  [/^\/buyitagain/, 'buy it again page'],
  [/^\/list\/view\/details\/shared\//, 'list details>shared'],
  [/^\/list\/view\/details\//, 'list details'],
  [/^\/gift-cards\//, 'giftcard'],
  [/^\/cart(\/|$)/, 'cart'],
  [/^\/bath-renovation/, 'bathroom design studio'],
  [/^\/configurator-projects/, 'flooring projects'],
  [/^\/projects\/.+/, 'project details'],
  [/^\/projects\/?$/, 'projects'],
  [/^\/?$/, 'homepage'],
];

const pageTypeFromPath = (path = '') => {
  const normalised = path.replace(/^\/render[^/]*/, '');
  return PAGE_TYPE_RULES.find(([pattern]) => pattern.test(normalised))?.[1] ?? 'unknown';
};

// The page type the site already reports to analytics — one value, used
// everywhere the assistant needs a page type. Read digitalData rather than
// window.ddo: ddo is an alias present on some experiences only (defined on /p/*,
// undefined on /b/*, /s/*, /c/*, /l/*), while digitalData was set on every page
// checked. The vocabulary belongs to each experience and is open-ended
// (tool:*, maml:*, collections:*), so never switch on an exhaustive list.
export const getPageType = () => {
  return window?.digitalData?.page?.pageInfo?.pageType || pageTypeFromPath(getPagePath());
};

// The closed vocabulary the planner assistant service still validates against
// (PlannerAssistantPageTypeInput). Intentionally separate from getPageType():
// this is the interim mapping until the service drops validation, at which point
// callers swap over to the open-ended analytics value.
export const getLegacyPagetype = () => {
  const path = getPagePath().replace(/^\/render[^/]*/, '');
  if (path === '/') return LEGACY_PAGE_TYPES.HOME;

  const [, segment, subSegment] = path.split('/');
  switch (segment) {
  case 'c':
    return LEGACY_PAGE_TYPES.CONTENT;
  case 'p':
    // /p/sets/* is a bundle; every other /p/ variant (rental, paint config) is
    // still a product page.
    return subSegment === 'sets' ? LEGACY_PAGE_TYPES.BIP : LEGACY_PAGE_TYPES.PIP;
  case 's':
  case 'b':
    // Both stay PLP. The enum also has Search and Browse, but splitting them
    // apart would change what the service already receives for this traffic —
    // out of scope while this getter is only a stepping stone.
    return LEGACY_PAGE_TYPES.PLP;
  case 'cart':
    return LEGACY_PAGE_TYPES.CART;
  case 'checkout':
  case 'mycheckout':
    return LEGACY_PAGE_TYPES.CHECKOUT;
  default:
    return LEGACY_PAGE_TYPES.HOME;
  }
};

export const getItemId = () => {
  // Path structure, not page type: an item id is the last segment of a /p/ URL.
  // Every product variant (pip, bip, rental, paint config) shares that shape.
  if (!/^\/p\//.test(getPagePath())) return null;

  return getPagePath().split('/').at(-1);
};

export const getRecentSearchTerms = () => {

  try {
    const history = THDStorageUtils.get(LOCAL_STORAGE, { key: 'header_search_history' });
    if (!history || history === 'undefined') return [];
    return JSON.parse(history).map((term) => {
      try {
        return decodeURIComponent(term.keyword);
      } catch {
        return term.keyword;
      }
    });

  } catch (error) {
    logger.error({
      message: `Error occurred while accessing local storage - ${'header_search_history'}`,
      error
    });
    return [];
  }
};
